// Grading figures for one BTVN task, from the marks already loaded. Import-free for node --test.

import type { StudentSubmission, SubmissionTask } from './submissions';

export interface MarkBand {
  label: string;
  count: number;
  color: string;
}

export interface GradingStats {
  submitted: number;
  graded: number;
  /** Submissions that carry a numeric mark. */
  marks: number;
  /** Null with no numeric mark. */
  average: number | null;
  bands: MarkBand[] | null;
}

// lower bounds, half-open: [0,5) [5,6.5) [6.5,8) [8,10]; the ramp matches the exam score colours
const BANDS: { label: string; min: number; color: string }[] = [
  { label: '<5', min: Number.NEGATIVE_INFINITY, color: '#EF4444' },
  { label: '5-6,5', min: 5, color: '#FF7F07' },
  { label: '6,5-8', min: 6.5, color: '#2563EB' },
  { label: '8-10', min: 8, color: '#4BCA81' },
];

export function bandIndex(mark: number): number {
  let index = 0;
  BANDS.forEach((band, i) => { if (mark >= band.min) index = i; });
  return index;
}

export function gradingStats(submissions: StudentSubmission[]): GradingStats {
  const marks = submissions.map((s) => s.mark).filter((mark): mark is number => typeof mark === 'number' && !isNaN(mark));
  const enough = marks.length > 0;
  const counts = BANDS.map(() => 0);
  marks.forEach((mark) => { counts[bandIndex(mark)] += 1; });
  return {
    submitted: submissions.length,
    graded: submissions.filter((s) => s.approved).length,
    marks: marks.length,
    average: enough ? Math.round((marks.reduce((sum, mark) => sum + mark, 0) / marks.length) * 10) / 10 : null,
    bands: enough ? BANDS.map((band, i) => ({ label: band.label, count: counts[i], color: band.color })) : null,
  };
}

export function taskGradingStats(task: SubmissionTask): GradingStats {
  return gradingStats(Object.keys(task.byCode).map((code) => task.byCode[code]));
}

/** "7,8": one decimal with the Vietnamese comma, like statusLabel's marks. */
export function formatAverage(value: number): string {
  return String(Math.round(value * 10) / 10).replace('.', ',');
}
